/**
 * SensitivitySelect — dropdown for picking a document's sensitivity level.
 *
 * Props:
 *   value: string (current level)
 *   onChange: (newLevel) => void
 *   autoDetected?: {level, matches[]}  — if provided, shows a suggestion banner
 */
import { Label } from '@/components/ui/label';
import { AlertTriangle, Sparkles } from 'lucide-react';

const LEVELS = [
  { value: 'public',       label: '🌐 Public — anyone can view' },
  { value: 'internal',     label: '🏢 Internal — all employees (default)' },
  { value: 'confidential', label: '🔒 Confidential — admin, manager, hr' },
  { value: 'legal',        label: '⚖️ Legal — admin, hr, legal' },
  { value: 'pii',          label: '👤 PII — admin, hr only' },
  { value: 'hipaa',        label: '🏥 HIPAA — admin, hr only' },
];

const SensitivitySelect = ({ value = 'internal', onChange, autoDetected, className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-[#6B7280] mb-1 block">Sensitivity</Label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 border border-[#E5E7EB] rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
      data-testid="sensitivity-select"
    >
      {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
    </select>
    {autoDetected && autoDetected.level && autoDetected.level !== value && (
      <div className="mt-2 flex items-start gap-2 p-2 bg-[#FF9500]/5 border border-[#FF9500]/20 rounded text-[11px] text-[#4B5563]" data-testid="sensitivity-suggestion">
        <Sparkles className="w-3 h-3 text-[#FF9500] flex-shrink-0 mt-0.5" />
        <div>
          <strong className="text-[#FF9500]">AI suggests:</strong> {autoDetected.level.toUpperCase()} — detected {autoDetected.matches?.join(', ') || 'sensitive content'}.
          <button
            type="button"
            onClick={() => onChange(autoDetected.level)}
            className="ml-1 underline text-[#002FA7]"
            data-testid="accept-sensitivity-suggestion"
          >
            Accept
          </button>
        </div>
      </div>
    )}
    {['pii', 'hipaa', 'legal'].includes(value) && (
      <div className="mt-2 flex items-start gap-2 p-2 bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded text-[11px] text-[#4B5563]">
        <AlertTriangle className="w-3 h-3 text-[#FF3B30] flex-shrink-0 mt-0.5" />
        <p>Only users with the appropriate role + explicitly-granted access will be able to view this document.</p>
      </div>
    )}
  </div>
);

export default SensitivitySelect;
